import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { Grid, Button } from 'semantic-ui-react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { sendNotification } from '../utils/notify';
import { buildValuationLead, CONDITION_LABELS, formatPriceFull } from '../utils/valuationLead';
import FadeInSection from './FadeInSection';
import ContactModal from './ContactModal';
import EmailSuggestion, { useEmailSuggestion } from './EmailSuggestion';
import '../styles/HomeValuation.scss';

// Fix default marker icons broken by webpack
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom marker icons
const subjectIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const compIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [20, 33],
  iconAnchor: [10, 33],
  popupAnchor: [1, -28],
  shadowSize: [41, 41],
});

// Condition multipliers (used only for the condition-bar visualization in Step 2)
const CONDITION_MULTIPLIERS = {
  excellent: 1.10,
  good: 1.03,
  fair: 0.95,
  poor: 0.85,
};

// ── Claude-powered valuation ──────────────────────────────────────────────────
// API key lives server-side in /api/valuation.js — never exposed to the browser
async function callClaudeValuation({ address, sqft, beds, baths, condition }) {
  const res = await fetch('/api/valuation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address, sqft, beds, baths, condition }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Valuation request failed');
  }

  return res.json();
}

// Use real coordinates from Rentcast when available, fall back to offset for missing ones
function attachCoords(comps, centerLat, centerLng) {
  return comps.map((comp, i) => {
    if (comp.lat && comp.lng) return comp;
    const angle = (i / comps.length) * 2 * Math.PI + Math.random() * 0.4;
    const distance = 0.003 + Math.random() * 0.008;
    return {
      ...comp,
      lat: centerLat + Math.cos(angle) * distance,
      lng: centerLng + Math.sin(angle) * distance,
    };
  });
}

// Format currency
function formatPrice(n) {
  return n >= 1000000
    ? `$${(n / 1000000).toFixed(2)}M`
    : `$${(n / 1000).toFixed(0)}K`;
}

// Geocode an address using OpenStreetMap Nominatim (free, no API key required)
async function geocodeAddress(address) {
  const encoded = encodeURIComponent(address);
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1&addressdetails=1`;
  const res = await fetch(url, {
    headers: { 'Accept-Language': 'en' },
  });
  const data = await res.json();
  if (!data || data.length === 0) throw new Error('Address not found. Please check and try again.');
  return {
    lat: parseFloat(data[0].lat),
    lng: parseFloat(data[0].lon),
    displayName: data[0].display_name,
  };
}

// ─── Main tool ────────────────────────────────────────────────────────────────
// `source` tags the lead notification + analytics so leads can be traced to the
// page they came from (e.g. 'home-valuation' vs 'westwood-highlands').
// `intro` optionally overrides the Step 1 header copy.
function ValuationTool({ source = 'home-valuation', intro }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address: '',
    sqft: '',
    beds: '',
    baths: '',
    condition: 'good',
  });

  const [results, setResults] = useState(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddressSelect = (address) => {
    setFormData((prev) => ({ ...prev, address }));
  };

  // Sends at most once per submit, so an error thrown after the success-path send
  // can't produce a second "estimate failed" notification for the same lead.
  // A failure is logged rather than shown: by the time this rejects the request has
  // been retried, and if it reached the server the lead is in Cloze and the logs.
  const leadSent = useRef(false);
  const sendLead = (outcome) => {
    if (leadSent.current) return;
    leadSent.current = true;
    sendNotification(buildValuationLead(formData, source, outcome)).catch((err) => {
      console.error('Valuation lead notification failed:', err);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    leadSent.current = false;
    setError('');
    setLoading(true);

    try {
      const sqft = parseInt(formData.sqft);

      // 1. Geocode first so we have the full resolved address (city, state, zip)
      //    to send to the AI — otherwise it guesses the wrong market
      const geo = await geocodeAddress(formData.address);

      // 2. Call AI with the full verified address from Nominatim
      const claudeResult = await callClaudeValuation({
        address: geo.displayName,
        sqft,
        beds: formData.beds,
        baths: formData.baths,
        condition: formData.condition,
      });

      // 2. Attach map coordinates to Claude's comps
      const comps = attachCoords(claudeResult.comps, geo.lat, geo.lng);
      const medianCompPricePerSqft = Math.round(
        comps.reduce((s, c) => s + c.pricePerSqft, 0) / comps.length
      );

      const { low, mid, high, pricePerSqft, reasoning } = claudeResult.estimate;

      setResults({
        geo,
        comps,
        estimatedPrice: mid,
        lowEstimate: low,
        highEstimate: high,
        sqft,
        medianCompPricePerSqft,
        estimatedPricePerSqft: pricePerSqft,
        reasoning,
      });

      // 3. Send the lead in the background — the visitor already has their estimate,
      //    so don't make them wait on it.
      sendLead({ estimate: { low, high, reasoning } });

      setStep(2);
      if (typeof window.gtag === 'function') window.gtag('event', 'home_valuation_submit', { source });
    } catch (err) {
      const message = err.message || 'Something went wrong. Please try again.';
      setError(message);
      // They gave us their name, email and address before the estimate failed —
      // that's still a lead, and Lily can follow up with a real valuation.
      sendLead({ error: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-valuation">
      {step === 1 && <StepOne formData={formData} onChange={handleChange} onAddressSelect={handleAddressSelect} onSubmit={handleSubmit} loading={loading} error={error} intro={intro} />}
      {step === 2 && results && <StepTwo formData={formData} results={results} onReset={() => { setStep(1); setResults(null); setError(''); }} isOpen={isOpen} setIsOpen={setIsOpen} />}
    </div>
  );
}

// ─── Field validation rules ───────────────────────────────────────────────────

function validateField(name, value) {
  switch (name) {
    case 'first_name':
      if (!value.trim()) return 'First name is required';
      if (value.trim().length < 2) return 'Please enter your first name';
      return null;
    case 'last_name':
      if (!value.trim()) return 'Last name is required';
      if (value.trim().length < 2) return 'Please enter your last name';
      return '';
    case 'email':
      if (!value.trim()) return 'Email address is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Please enter a valid email address';
      return '';
    case 'address':
      if (!value.trim()) return 'Property address is required';
      if (!/\d/.test(value)) return 'Please include a street number (e.g. 123 Main St)';
      if (value.trim().split(' ').length < 3) return 'Please enter a full address including city and state';
      return '';
    case 'sqft':
      if (!value) return 'Square footage is required';
      if (isNaN(parseInt(value)) || parseInt(value) < 200 || parseInt(value) > 20000)
        return 'Enter a value between 200 and 20,000 sq ft';
      return '';
    case 'beds':
      if (!value) return 'Bedrooms is required';
      if (isNaN(parseInt(value)) || parseInt(value) < 1 || parseInt(value) > 20)
        return 'Enter a value between 1 and 20';
      return '';
    case 'baths':
      if (!value) return 'Bathrooms is required';
      if (isNaN(parseFloat(value)) || parseFloat(value) < 1 || parseFloat(value) > 20)
        return 'Enter a value between 1 and 20';
      return '';
    default:
      return '';
  }
}

// ─── Step 1: Form ─────────────────────────────────────────────────────────────

function StepOne({ formData, onChange, onAddressSelect, onSubmit, loading, error, intro }) {
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const emailHint = useEmailSuggestion(formData.email);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  const conditionOptions = [
    { value: 'excellent', label: 'Excellent — Move-in ready, recent updates' },
    { value: 'good', label: 'Good — Well-maintained, minor wear' },
    { value: 'fair', label: 'Fair — Some deferred maintenance' },
    { value: 'poor', label: 'Poor / Needs Work — Major repairs needed' },
  ];

  // Request user location on mount to bias autocomplete results
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {} // silently ignore if denied
      );
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleAddressChange = (e) => {
    onChange(e);
    setAddressConfirmed(false);

    const value = e.target.value;
    clearTimeout(debounceRef.current);

    if (value.length < 5) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setAddressSearching(true);
      try {
        // Build viewbox around user's location (±0.3 degrees ≈ ~20 miles) if available
        const locationParam = userCoords
          ? `&viewbox=${userCoords.lng - 0.3},${userCoords.lat + 0.3},${userCoords.lng + 0.3},${userCoords.lat - 0.3}&bounded=0`
          : '';
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=5&addressdetails=1&countrycodes=us${locationParam}`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setSuggestions(data.slice(0, 5));
        setShowSuggestions(data.length > 0);
      } catch (_) {
        setSuggestions([]);
      } finally {
        setAddressSearching(false);
      }
    }, 250);
  };

  const handleSelectSuggestion = (suggestion) => {
    const { address: a } = suggestion;
    const parts = [
      a.house_number,
      a.road,
      a.city || a.town || a.village || a.county,
      a.state,
      a.postcode,
    ].filter(Boolean);
    const formatted = parts.length >= 3 ? parts.join(', ') : suggestion.display_name;

    onAddressSelect(formatted);
    setAddressConfirmed(true);
    setSuggestions([]);
    setShowSuggestions(false);
    setFieldErrors((prev) => ({ ...prev, address: '' }));
    setTouched((prev) => ({ ...prev, address: true }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const required = ['first_name', 'last_name', 'email', 'address', 'sqft', 'beds', 'baths'];
    const newErrors = {};
    let hasErrors = false;

    required.forEach((name) => {
      const err = validateField(name, formData[name]);
      newErrors[name] = err;
      if (err) hasErrors = true;
    });

    setFieldErrors(newErrors);
    setTouched({ name: true, email: true, address: true });

    if (!hasErrors) onSubmit(e);
  };

  const showError = (name) => touched[name] && fieldErrors[name];

  const formatSuggestionLabel = (s) => {
    const a = s.address || {};
    const parts = [
      [a.house_number, a.road].filter(Boolean).join(' '),
      a.city || a.town || a.village || a.county,
      a.state,
    ].filter(Boolean);
    return parts.join(', ') || s.display_name;
  };

  return (
    <div className="hv-step hv-step--form">
      <FadeInSection>
        <div className="hv-header">
          <p className="hv-eyebrow">{intro?.eyebrow || 'Instant Estimate'}</p>
          <h1>{intro?.title || 'What Is Your Home Worth?'}</h1>
          <p className="hv-subheading">
            {intro?.subheading || (
              <>Enter your details below to see a personalized valuation based on
              recent comparable sales in your neighborhood.</>
            )}
          </p>
        </div>
      </FadeInSection>

      <FadeInSection>
        <form className="hv-form" onSubmit={handleFormSubmit} noValidate>
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={8}>
                <div className={`hv-field ${showError('first_name') ? 'hv-field--error' : ''}`}>
                  <label htmlFor="hv-first-name">First Name</label>
                  <input
                    id="hv-first-name"
                    name="first_name"
                    type="text"
                    placeholder="Jane"
                    value={formData.first_name}
                    onChange={onChange}
                    onBlur={handleBlur}
                    autoComplete="given-name"
                  />
                  {showError('first_name') && <span className="hv-field-error">{fieldErrors.first_name}</span>}
                </div>
              </Grid.Column>
              <Grid.Column width={8}>
                <div className={`hv-field ${showError('last_name') ? 'hv-field--error' : ''}`}>
                  <label htmlFor="hv-last-name">Last Name</label>
                  <input
                    id="hv-last-name"
                    name="last_name"
                    type="text"
                    placeholder="Smith"
                    value={formData.last_name}
                    onChange={onChange}
                    onBlur={handleBlur}
                    autoComplete="family-name"
                  />
                  {showError('last_name') && <span className="hv-field-error">{fieldErrors.last_name}</span>}
                </div>
              </Grid.Column>
              <Grid.Column width={8}>
                <div className={`hv-field ${showError('email') ? 'hv-field--error' : ''}`}>
                  <label htmlFor="hv-email">Email Address</label>
                  <input
                    id="hv-email"
                    name="email"
                    type="email"
                    placeholder="jane@example.com"
                    value={formData.email}
                    onChange={onChange}
                    onBlur={(e) => { handleBlur(e); emailHint.onBlur(); }}
                    autoComplete="email"
                  />
                  <EmailSuggestion
                    suggestion={emailHint.suggestion}
                    onAccept={(value) => onChange({ target: { name: 'email', value } })}
                  />
                  {showError('email') && <span className="hv-field-error">{fieldErrors.email}</span>}
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <div
                  className={`hv-field hv-field--address ${showError('address') ? 'hv-field--error' : ''}`}
                  ref={suggestionsRef}
                >
                  <label htmlFor="hv-address">
                    Property Address
                    {addressSearching && <span className="hv-addr-searching"> searching…</span>}
                    {addressConfirmed && !addressSearching && (
                      <span className="hv-addr-confirmed"> ✓ address confirmed</span>
                    )}
                  </label>
                  <input
                    id="hv-address"
                    name="address"
                    type="text"
                    placeholder="123 Main St, San Francisco, CA 94110"
                    value={formData.address}
                    onChange={handleAddressChange}
                    onBlur={(e) => {
                      // Delay so a suggestion click registers before blur closes dropdown
                      setTimeout(() => {
                        handleBlur(e);
                        setShowSuggestions(false);
                      }, 150);
                    }}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    autoComplete="off"
                    className={addressConfirmed ? 'hv-input--confirmed' : ''}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="hv-suggestions">
                      {suggestions.map((s, i) => (
                        <li
                          key={i}
                          className="hv-suggestion-item"
                          onMouseDown={() => handleSelectSuggestion(s)}
                        >
                          <span className="hv-suggestion-primary">{formatSuggestionLabel(s)}</span>
                          {s.address?.postcode && (
                            <span className="hv-suggestion-secondary">{s.address.postcode}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {showError('address') && <span className="hv-field-error">{fieldErrors.address}</span>}
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={6}>
                <div className={`hv-field ${showError('sqft') ? 'hv-field--error' : ''}`}>
                  <label htmlFor="hv-sqft">Square Footage</label>
                  <input
                    id="hv-sqft"
                    name="sqft"
                    type="number"
                    placeholder="e.g. 1800"
                    min="200"
                    max="20000"
                    value={formData.sqft}
                    onChange={onChange}
                    onBlur={handleBlur}
                  />
                  {showError('sqft') && <span className="hv-field-error">{fieldErrors.sqft}</span>}
                </div>
              </Grid.Column>
              <Grid.Column width={5}>
                <div className={`hv-field ${showError('beds') ? 'hv-field--error' : ''}`}>
                  <label htmlFor="hv-beds">Bedrooms</label>
                  <input
                    id="hv-beds"
                    name="beds"
                    type="number"
                    placeholder="e.g. 3"
                    min="1"
                    max="20"
                    value={formData.beds}
                    onChange={onChange}
                    onBlur={handleBlur}
                  />
                  {showError('beds') && <span className="hv-field-error">{fieldErrors.beds}</span>}
                </div>
              </Grid.Column>
              <Grid.Column width={5}>
                <div className={`hv-field ${showError('baths') ? 'hv-field--error' : ''}`}>
                  <label htmlFor="hv-baths">Bathrooms</label>
                  <input
                    id="hv-baths"
                    name="baths"
                    type="number"
                    placeholder="e.g. 2"
                    min="1"
                    max="20"
                    step="0.5"
                    value={formData.baths}
                    onChange={onChange}
                    onBlur={handleBlur}
                  />
                  {showError('baths') && <span className="hv-field-error">{fieldErrors.baths}</span>}
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <div className="hv-field">
                  <label htmlFor="hv-condition">Home Condition</label>
                  <select
                    id="hv-condition"
                    name="condition"
                    value={formData.condition}
                    onChange={onChange}
                  >
                    {conditionOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </Grid.Column>
            </Grid.Row>

            {error && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <div className="hv-error">{error}</div>
                </Grid.Column>
              </Grid.Row>
            )}

            <Grid.Row>
              <Grid.Column width={16}>
                <Button
                  className="hv-submit-btn"
                  type="submit"
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Analyzing with AI...' : 'Get My Home Valuation'}
                </Button>
                <p className="hv-ai-badge">Powered by Claude AI</p>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </form>
      </FadeInSection>

      <div className="hv-disclaimer">
        <p>
          This estimate is based on publicly available data and recent comparable sales.
          It is not a formal appraisal. Contact Lily for a comprehensive, in-person evaluation.
        </p>
      </div>
    </div>
  );
}

// ─── Step 2: Results ──────────────────────────────────────────────────────────

function StepTwo({ formData, results, onReset, isOpen, setIsOpen }) {
  const { geo, comps, estimatedPrice, lowEstimate, highEstimate, sqft, medianCompPricePerSqft, estimatedPricePerSqft, reasoning } = results;

  const conditionLabel = CONDITION_LABELS[formData.condition];

  return (
    <div className="hv-step hv-step--results">
      {/* Header */}
      <FadeInSection>
        <div className="hv-header">
          <p className="hv-eyebrow">Your Home Valuation</p>
          <h1>Estimated Value Range</h1>
          <p className="hv-address-display">{formData.address}</p>
        </div>
      </FadeInSection>

      {/* Price Range Banner */}
      <FadeInSection>
        <div className="hv-price-banner">
          <div className="hv-price-range">
            <div className="hv-price-low">
              <span className="hv-price-label">Low Estimate</span>
              <span className="hv-price-value">{formatPrice(lowEstimate)}</span>
            </div>
            <div className="hv-price-center">
              <span className="hv-price-label">Estimated Value</span>
              <span className="hv-price-main">{formatPrice(estimatedPrice)}</span>
            </div>
            <div className="hv-price-high">
              <span className="hv-price-label">High Estimate</span>
              <span className="hv-price-value">{formatPrice(highEstimate)}</span>
            </div>
          </div>
          <div className="hv-price-meta">
            <span>Condition: <strong>{conditionLabel}</strong></span>
            <span>·</span>
            <span>Est. {sqft.toLocaleString()} sq ft</span>
            <span>·</span>
            <span>~${estimatedPricePerSqft}/sq ft</span>
          </div>
          {reasoning && <p className="hv-price-reasoning">{reasoning}</p>}
        </div>
      </FadeInSection>

      {/* Map */}
      <FadeInSection>
        <div className="hv-section">
          <h2>Your Property &amp; Nearby Comparable Sales</h2>
          <p className="hv-section-desc">
            The <span className="hv-legend-subject">red marker</span> is your property.{' '}
            <span className="hv-legend-comp">Green markers</span> are homes sold within the past 3 months.
          </p>
          <div className="hv-map-container">
            <MapContainer
              center={[geo.lat, geo.lng]}
              zoom={14}
              scrollWheelZoom={false}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Subject property */}
              <Marker position={[geo.lat, geo.lng]} icon={subjectIcon}>
                <Popup>
                  <div className="hv-popup">
                    <strong>Your Property</strong>
                    <br />
                    {formData.address}
                    <br />
                    <em>Est. {formatPrice(estimatedPrice)}</em>
                  </div>
                </Popup>
              </Marker>

              {/* Search radius */}
              <Circle
                center={[geo.lat, geo.lng]}
                radius={800}
                pathOptions={{ color: '#c8a96e', fillColor: '#c8a96e', fillOpacity: 0.06, weight: 1.5, dashArray: '4 6' }}
              />

              {/* Comparable sales */}
              {comps.map((comp, i) => (
                <Marker key={i} position={[comp.lat, comp.lng]} icon={compIcon}>
                  <Popup>
                    <div className="hv-popup">
                      <strong>{comp.address}</strong>
                      <br />
                      Sold {comp.daysAgo} days ago
                      <br />
                      <em>{formatPriceFull(comp.price)}</em>
                      <br />
                      {comp.beds}bd / {comp.baths}ba · {comp.sqft.toLocaleString()} sq ft
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </FadeInSection>

      {/* Comparable Sales Table */}
      <FadeInSection>
        <div className="hv-section">
          <h2>Recent Comparable Sales</h2>
          <p className="hv-section-desc">Real sales data — similar homes sold within the past 90 days.</p>
          <div className="hv-table-wrapper">
            <table className="hv-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Sale Price</th>
                  <th>Sq Ft</th>
                  <th>Beds / Baths</th>
                  <th>$/Sq Ft</th>
                  <th>Days Ago</th>
                </tr>
              </thead>
              <tbody>
                {comps.map((comp, i) => (
                  <tr key={i}>
                    <td>{comp.address}</td>
                    <td className="hv-table-price">{formatPriceFull(comp.price)}</td>
                    <td>{comp.sqft.toLocaleString()}</td>
                    <td>{comp.beds} bd / {comp.baths} ba</td>
                    <td>${comp.pricePerSqft}</td>
                    <td>{comp.daysAgo}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4"><strong>Median Price / Sq Ft</strong></td>
                  <td colSpan="2"><strong>${medianCompPricePerSqft}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </FadeInSection>

      {/* Condition Breakdown */}
      <FadeInSection>
        <div className="hv-section hv-condition-section">
          <h2>How Condition Affects Value</h2>
          <div className="hv-condition-bars">
            {Object.entries(CONDITION_MULTIPLIERS).map(([key, mult]) => {
              const adjusted = Math.round((sqft * medianCompPricePerSqft * mult) / 5000) * 5000;
              const isActive = key === formData.condition;
              const pct = Math.round((mult - 0.85) / (1.10 - 0.85) * 100);
              return (
                <div key={key} className={`hv-condition-bar ${isActive ? 'hv-condition-bar--active' : ''}`}>
                  <div className="hv-condition-bar-label">{CONDITION_LABELS[key]}</div>
                  <div className="hv-condition-bar-track">
                    <div className="hv-condition-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="hv-condition-bar-value">{formatPrice(adjusted)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </FadeInSection>

      {/* CTA */}
      <FadeInSection>
        <div className="hv-cta-section">
          <h2>Ready for a Precise Valuation?</h2>
          <p>
            This estimate is a starting point. Lily will provide a full comparative market analysis,
            taking into account your home's unique features, recent upgrades, and current market conditions.
          </p>
          <div className="hv-cta-buttons">
            <ContactModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              trigger={<button className="hv-cta-btn hv-cta-btn--primary" onClick={() => setIsOpen(true)}>Schedule a Free Consultation</button>}
            />
            <button className="hv-cta-btn hv-cta-btn--secondary" onClick={onReset}>
              Value Another Property
            </button>
          </div>
        </div>
      </FadeInSection>
    </div>
  );
}

export default ValuationTool;
